// core/stores/caseStore.ts — Zustand store for moderation cases.
//
// Hydrated at module init from the typed seed (seedCases). Services mutate this
// store; hooks read it reactively. Components must use hooks, not this store.
//
// Replaces the legacy in-module `cases` array + `case:change` DOM event pattern
// from src/scripts/data/cases.js. Reactivity is provided by Zustand subscriptions.

import { create } from 'zustand';
import type { CaseRecord } from '@/core/models/case';
import { seedCases } from '@/core/data/cases';
import { bootSeed } from './bootSeed';

interface CaseState {
  cases: CaseRecord[];

  /** Replace the entire cases list (used by _resetForTest). */
  setCases: (cases: CaseRecord[]) => void;

  /** Append a new case. */
  addCase: (c: CaseRecord) => void;

  /** Apply a patch to a single case by id; no-op if not found. */
  patchCase: (id: string, patch: Partial<CaseRecord>) => void;
}

export const useCaseStore = create<CaseState>()((set) => ({
  // Deep-clone the seed so test resets don't mutate the imported const.
  // Empty in the running app (see bootSeed.ts); the seed only exists under the test runner.
  cases: bootSeed(seedCases).map((c) => ({ ...c, votes: { ...c.votes } })),

  setCases: (cases) => set({ cases }),

  addCase: (c) => set((s) => ({ cases: [...s.cases, c] })),

  patchCase: (id, patch) =>
    set((s) => ({
      cases: s.cases.map((c) => (c.id === id ? { ...c, ...patch } : c)),
    })),
}));
